import { toastError, toastSuccess } from '@onestep/coreui/Toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { isEmpty, isString, omit, omitBy } from 'lodash';
import { useParams } from 'react-router';

import useMixpanel from '../../../../hooks/useMixpanel';
import useSinglePatientService from '../../../../Pages/SinglePatientDetails/Service/index';
import { replaceStringNull } from '../../../../utils/noneOptionUtil';
import { invalidatePatientOverviewData } from '../../../patientOverviewData';
import { formatTime, substract } from '../../../timeService';
import { dateTimeFormat, unitOfTime } from '../../../timeService/types';
import useAdministrationService from '../administration';
import useTypeOfCareService from '../typeOfCareService/index';
import type { IEpisodeOfCare, INewEpisodeOfCare } from '../types';

export default function useNewEpisodeofCareService() {
  const queryClient = useQueryClient();
  const { patientUuid = '' } = useParams<{ patientUuid: string }>();
  const administration = useAdministrationService();
  const { refetch } = useSinglePatientService(patientUuid);
  const { trackFail } = useMixpanel();
  const { createTypeOfCare } = useTypeOfCareService();

  const onCreateSucceeded = async (newEpisodeData: Partial<INewEpisodeOfCare>, eocUuid: string) => {
    const createLocationUrl = getCreateLocationUrl(newEpisodeData);
    const billingPeriodUrl = getBillingPeriodUrl(newEpisodeData);
    const createTOCUrl = getCreateTOCUrl(eocUuid, newEpisodeData?.typeOfCareUuid);
    await Promise.all([createLocationUrl, billingPeriodUrl, createTOCUrl]);
    await invalidateAdministrationQuery();
    await refetchPatientQuery();
  };

  const createAction = useMutation({
    mutationFn: async (data: Partial<IEpisodeOfCare>) => {
      try {
        const result = await axios.post(
          `/api/camel/patient_profile/administration/episode_of_care`,
          {
            ...omit(data, ['careLocationName', 'refDate', 'rtm', 'rtmDate']),
            patientUuid,
          },
        );
        await onCreateSucceeded(data, result?.data?.uuid);
        toastSuccess({ content: 'Created successfully' });
      } catch (err) {
        trackFail('create_new_episode', { err });
        toastError({ content: 'Failed to start a new episode!' });
      }
    },
  });

  const invalidateAdministrationQuery = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['camel', 'patient_profile', 'administration', patientUuid],
    });
  };

  const getCreateTOCUrl = (eocUuid: string, typeOfCareUuid?: string | null) => {
    const typeUuid = replaceStringNull(typeOfCareUuid) as string | null;
    if (typeUuid) {
      try {
        return createTypeOfCare({
          eocUuid,
          typeUuid,
        });
      } catch (err) {
        trackFail('create_type_of_care', { err });
      }
    }
  };

  const getBillingPeriodUrl = (newEpisodeData: Partial<INewEpisodeOfCare>) => {
    if (newEpisodeData.rtm !== undefined) {
      try {
        return administration.toggleBillingPeriod({
          data: {
            ...(newEpisodeData.rtm
              ? {
                  startDate: formatTime(newEpisodeData.rtmDate, dateTimeFormat.SERVER_DATE_FORMAT),
                }
              : {
                  endDate: formatTime(
                    substract(unitOfTime.days, 1),
                    dateTimeFormat.SERVER_DATE_FORMAT,
                  ),
                }),
          },
        });
      } catch (err) {
        trackFail('toggle_billing_period', { err });
        toastError({ content: 'Failed to toggle billing period!' });
      }
    }
  };

  const getCreateLocationUrl = (newEpisodeData: Partial<INewEpisodeOfCare>) => {
    if (newEpisodeData.careLocationName) {
      try {
        return administration.moveCareLocation({
          data: {
            careLocationName: newEpisodeData.careLocationName,
            refDate: newEpisodeData?.refDate || formatTime('', dateTimeFormat.SERVER_DATE_FORMAT),
          },
        });
      } catch (err) {
        trackFail('create_care_location', { err });
        toastError({ content: 'Failed to create a care location!' });
      }
    }
  };

  const endCurrentEpisode = async () => {
    if (!isEmpty(administration?.data?.currentEpisodeOfCare)) {
      await administration.editEpisodeOfCare({
        data: {
          ...administration?.data?.currentEpisodeOfCare,
          end: formatTime(substract(unitOfTime.days, 1), dateTimeFormat.SERVER_DATE_FORMAT),
        } as INewEpisodeOfCare,
      });
    }
  };

  const createNewEpisode = async (newEpisodeData: Partial<INewEpisodeOfCare>) => {
    try {
      await endCurrentEpisode();
      await createAction.mutateAsync(
        omitBy(newEpisodeData, (value) => isString(value) && isEmpty(value)),
      );
    } catch (err) {
      trackFail('end_current_episode', { err });
      toastError({ content: 'Failed to end a current episode!' });
    }
  };

  const refetchPatientQuery = () => {
    refetch();
    invalidatePatientOverviewData(queryClient, patientUuid);
  };

  return {
    createNewEpisode,
  };
}
